import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { useLenis } from "../hooks/use-lenis";

export function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

import { SEOHead } from "@/components/site/SEOHead";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "KMWA — Kris Media Work Advertising · 360° Digital Agency" },
      { name: "description", content: "KMWAdvertising LLP is a full-funnel digital advertising and media buying studio delivering data-driven ROI, branding, performance marketing, and creative production." },
      { name: "keywords", content: "advertising agency, digital marketing, media buying, performance marketing, branding agency Mumbai, 360 advertising, Kris Media Works, KMWA" },
      { name: "author", content: "KMWAdvertising LLP" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { property: "og:title", content: "KMWA — Kris Media Work Advertising · 360° Digital Agency" },
      { property: "og:description", content: "KMWAdvertising LLP is a full-funnel digital advertising and media buying studio delivering data-driven ROI, branding, performance marketing, and creative production." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://kmwadvertising.com" },
      { property: "og:image", content: "https://kmwadvertising.com/images/kmwa_logo.png" },
      { property: "og:site_name", content: "KMWAdvertising LLP" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/images/kmwa_logo.png" },
      { rel: "shortcut icon", type: "image/png", href: "/images/kmwa_logo.png" },
      { rel: "apple-touch-icon", href: "/images/kmwa_logo.png" },
      { rel: "canonical", href: "https://kmwadvertising.com" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

export function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

export function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const pathname = router.state.location.pathname;
  useLenis();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleHashRedirect = () => {
      const hash = window.location.hash.toLowerCase();
      if (!hash) return;

      const hashToRouteMap: Record<string, string> = {
        "#process": "/process",
        "#work": "/work",
        "#industries": "/work",
        "#contact": "/contact-us",
        "#services": "/services",
        "#about": "/about-us",
        "#about-us": "/about-us",
      };

      const targetRoute = hashToRouteMap[hash];
      if (targetRoute) {
        window.history.replaceState(null, "", targetRoute);
        router.navigate({ to: targetRoute as any });
      }
    };

    handleHashRedirect();
    window.addEventListener("hashchange", handleHashRedirect);
    return () => window.removeEventListener("hashchange", handleHashRedirect);
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <SEOHead pathname={pathname} />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
